import { apiClient } from "@/shared/api/client"
import type {
  FarmUserResponse,
  FarmUserCreateRequest,
  FarmUserUpdateRequest,
  ResetFarmUserPasswordRequest,
  PaginatedResponse,
  PaginationParams,
} from "@/shared/types/api"

interface ListTeamParams extends PaginationParams {
  role?: string
  status?: string
  search?: string
}

export const teamApi = {
  /**
   * List team members for a farm
   */
  list: async (farmId: string, params?: ListTeamParams): Promise<PaginatedResponse<FarmUserResponse>> => {
    const response = await apiClient.get<PaginatedResponse<FarmUserResponse>>(
      `/farms/${farmId}/team`,
      { params }
    )
    return response.data
  },

  /**
   * Get a specific team member
   */
  get: async (farmId: string, farmUserId: string): Promise<FarmUserResponse> => {
    const response = await apiClient.get<{ data: FarmUserResponse }>(
      `/farms/${farmId}/team/${farmUserId}`
    )
    return response.data.data
  },

  /**
   * Create a new team member
   */
  create: async (farmId: string, data: FarmUserCreateRequest): Promise<FarmUserResponse> => {
    const response = await apiClient.post<{ data: FarmUserResponse }>(
      `/farms/${farmId}/team`,
      data
    )
    return response.data.data
  },

  /**
   * Update an existing team member
   */
  update: async (
    farmId: string,
    farmUserId: string,
    data: FarmUserUpdateRequest
  ): Promise<FarmUserResponse> => {
    const response = await apiClient.patch<{ data: FarmUserResponse }>(
      `/farms/${farmId}/team/${farmUserId}`,
      data
    )
    return response.data.data
  },

  /**
   * Delete (deactivate) a team member
   */
  delete: async (farmId: string, farmUserId: string): Promise<void> => {
    await apiClient.delete(`/farms/${farmId}/team/${farmUserId}`)
  },

  /**
   * Reset a team member's password (admin only)
   */
  resetPassword: async (
    farmId: string,
    farmUserId: string,
    data: ResetFarmUserPasswordRequest
  ): Promise<void> => {
    await apiClient.post(`/farms/${farmId}/team/${farmUserId}/reset-password`, data)
  },
}
